// eWings Lead Desk -- the admin, counsellor and telecaller views on one page.
//
// The flow it serves:  leads arrive  ->  the ADMIN assigns each to a COUNSELLOR  ->  the counsellor hands it to one of THEIR
// telecallers  ->  the telecaller calls (or WhatsApps) and logs an outcome. A final outcome locks the student for the telecaller.
// Each person sees only their own slice (the server enforces it; the page only mirrors it).
//
// Who the person is comes from the host: a signed-in identity (the standalone server) fixes the role and name, and only an admin
// may preview the other views. With no identity (inside the CRM, until roles are wired to workspace members) the role chooser stays.
import { defineFrontComponent } from 'twenty-sdk/define';
import { useColorScheme } from 'twenty-sdk/front-component';
import { useState } from 'react';

import type { DeskContext } from 'src/front-components/lead-desk/context';
import { useHostIdentity } from 'src/front-components/lead-desk/host';
import { Icon } from 'src/front-components/lead-desk/icons';
import { useNarrow } from 'src/front-components/lead-desk/narrow';
import { AddStudentScreen } from 'src/front-components/lead-desk/screens-add';
import { HomeScreen, OverviewScreen } from 'src/front-components/lead-desk/screens-home';
import { CallQueueScreen } from 'src/front-components/lead-desk/screens-calls';
import { AccountScreen } from 'src/front-components/lead-desk/screens-account';
import { FoldersScreen } from 'src/front-components/lead-desk/screens-folders';
import { WebLeadsScreen } from 'src/front-components/lead-desk/screens-more';
import { ReportsScreen } from 'src/front-components/lead-desk/screens-reports';
import { Sidebar, SIDEBAR_KEY } from 'src/front-components/lead-desk/sidebar';
import { StudentsScreen } from 'src/front-components/lead-desk/screens-students';
import { UsersScreen } from 'src/front-components/lead-desk/screens-users';
import { PageBody, ShellCard, ShellEmptyState } from 'src/front-components/theme/page-chrome';
import { ErrorLine } from 'src/front-components/theme/desk-controls';
import { BRAND } from 'src/front-components/theme/ewings-theme';
import { SHELL_SPACE } from 'src/front-components/theme/shell-tokens';
import { useLiveData } from 'src/front-components/utils/desk-data';
import { fetchRoster, fetchStudents } from 'src/front-components/utils/lead-desk-data';
import { fetchTeam } from 'src/front-components/utils/lead-desk-store';
import { PAGE_IDS } from 'src/constants/ewings-identifiers';
import { ROLE_PROFILES, visibleStudents, isAdmin, isSuperAdmin, type DeskRole, type DeskScreen, type UserRow } from 'src/utils/lead-desk';

type Saved = { collapsed: boolean; groupOpen: boolean };

const readSaved = (): Saved => {
  try {
    const raw = localStorage.getItem(SIDEBAR_KEY);
    const parsed = raw === null ? {} : (JSON.parse(raw) as Partial<Saved>);

    return { collapsed: parsed.collapsed === true, groupOpen: parsed.groupOpen !== false };
  } catch {
    return { collapsed: false, groupOpen: true };
  }
};

const LeadDesk = () => {
  const scheme = useColorScheme();
  const identity = useHostIdentity();
  // A signed-in person has a fixed role; an admin may flip this to preview another view. Without an identity it is the chooser.
  const [previewRole, setPreviewRole] = useState<DeskRole | null>(null);
  const [chosenRole, setChosenRole] = useState<DeskRole>('ADMIN');
  const [person, setPerson] = useState('');
  const [screen, setScreen] = useState<DeskScreen>('HOME');
  const [hint, setHint] = useState('');
  const [ui, setUi] = useState<Saved>(readSaved);
  const narrow = useNarrow();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isAnyAdmin = identity !== null && isAdmin(identity.role);
  const role: DeskRole = identity === null ? chosenRole : isAnyAdmin ? (previewRole ?? identity.role) : identity.role;
  const profile = ROLE_PROFILES[role] || ROLE_PROFILES['ADMIN'];
  const previewing = isAnyAdmin && previewRole !== null && previewRole !== identity.role;

  const updateUi = (next: Saved): void => {
    setUi(next);

    try {
      localStorage.setItem(SIDEBAR_KEY, JSON.stringify(next));
    } catch {
      // storage can be unavailable (private mode, sandbox); the choice still applies for this visit
    }
  };

  const roster = useLiveData(fetchRoster, 120_000);
  const team = useLiveData<UserRow[]>(identity === null ? () => Promise.resolve([]) : fetchTeam, 60_000);
  const counsellors = roster.data?.counsellors ?? [];
  const telecallers = roster.data?.telecallers ?? [];
  const listFor = role === 'COUNSELLOR' ? counsellors : role === 'TELECALLER' ? telecallers : [];

  // Who the view is for. A signed-in counsellor or telecaller is always themself; an admin previewing, or the CRM chooser, picks a name.
  const own = identity !== null && !isAdmin(identity.role) ? identity.name : '';
  const actingAs = profile.scope === 'ALL' ? '' : own !== '' ? own : listFor.includes(person) ? person : (listFor[0] ?? '');

  const students = useLiveData(() => fetchStudents(role, actingAs), 15_000);
  const scoped = visibleStudents(students.data?.rows ?? [], role, { name: actingAs });

  const ctx: DeskContext = {
    scheme,
    role,
    profile,
    person: actingAs,
    students: scoped,
    totalStudents: students.data?.total ?? 0,
    truncated: students.data?.truncated ?? false,
    counsellors,
    telecallers,
    people: team.data ?? [],
    refresh: async () => {
      await Promise.all([students.refresh(), roster.refresh(), team.refresh()]);
    },
    go: (next, nextHint = '') => {
      setScreen(next);
      setHint(nextHint);
      setDrawerOpen(false);
    },
    hint,
    identity,
  };

  const chooseRole = (next: DeskRole): void => {
    if (identity === null) setChosenRole(next);
    else setPreviewRole(identity.role === next ? null : next);

    setPerson('');
    setScreen('HOME');
  };

  // A role never lands on a screen it does not have (e.g. after switching away from Admin).
  const active: DeskScreen = profile.screens.includes(screen) ? screen : 'HOME';

  const body = (): React.ReactNode => {
    if (profile.scope !== 'ALL' && actingAs === '') {
      return (
        <ShellCard scheme={scheme}>
          <ShellEmptyState scheme={scheme} message={`There is no ${profile.label.toLowerCase()} to show yet.`} hint="Add one in Manage Users, then assign students to them." />
        </ShellCard>
      );
    }

    switch (active) {
      case 'OVERVIEW': return <OverviewScreen ctx={ctx} name={actingAs} />;
      case 'HOME': return role === 'TELECALLER' ? <CallQueueScreen ctx={ctx} /> : <HomeScreen ctx={ctx} />;
      case 'STUDENTS': return <StudentsScreen ctx={ctx} />;
      case 'ADD': return <AddStudentScreen ctx={ctx} />;
      case 'REPORTS': return <ReportsScreen ctx={ctx} />;
      case 'WEB_LEADS': return <WebLeadsScreen ctx={ctx} />;
      case 'USERS': return <UsersScreen ctx={ctx} />;
      case 'FOLDERS': return <FoldersScreen ctx={ctx} />;
      default: return <AccountScreen ctx={ctx} />;
    }
  };

  const sidebar = (
    <Sidebar
      scheme={scheme}
      role={role}
      active={active}
      collapsed={narrow ? false : ui.collapsed}
      groupOpen={ui.groupOpen}
      actingAs={actingAs}
      people={listFor}
      previewing={previewing}
      slideOver={narrow}
      onToggleCollapsed={() => (narrow ? setDrawerOpen(false) : updateUi({ ...ui, collapsed: !ui.collapsed }))}
      onToggleGroup={() => updateUi({ ...ui, groupOpen: !ui.groupOpen })}
      onGo={(next) => ctx.go(next)}
      onRole={chooseRole}
      onPerson={(name) => {
        setPerson(name);
        setScreen('HOME');
        setDrawerOpen(false);
      }}
    />
  );

  const content = (
    <main style={{ flex: 1, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
      {previewing && (
        <div style={{ marginBottom: SHELL_SPACE[3], padding: `${SHELL_SPACE[1.5]} ${SHELL_SPACE[3]}`, borderRadius: 'var(--t-border-radius-sm)', background: 'var(--t-tag-background-yellow)', color: 'var(--t-tag-text-yellow)', fontSize: 'var(--t-font-size-sm)' }}>
          You are previewing the {profile.label.toLowerCase()} view{actingAs !== '' ? ` of ${actingAs}` : ''}. Edits you make are real.{' '}
          <button type="button" onClick={() => chooseRole((identity?.role as DeskRole) ?? 'ADMIN')} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textDecoration: 'underline', font: 'inherit' }}>
            Back to admin
          </button>
        </div>
      )}
      <ErrorLine scheme={scheme} message={students.error} />
      {body()}
    </main>
  );

  // Narrow window (a phone): a slim top bar with the menu button, and the sidebar slides over the page instead of beside it.
  if (narrow) {
    return (
      <PageBody scheme={scheme}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[3], margin: `calc(-1 * ${SHELL_SPACE[6]}) calc(-1 * ${SHELL_SPACE[6]}) ${SHELL_SPACE[3]}`, padding: `${SHELL_SPACE[2]} ${SHELL_SPACE[3]}`, borderBottom: '1px solid var(--t-border-color-medium)', position: 'sticky', top: 0, zIndex: 40, background: 'var(--t-background-primary)' }}>
          <button type="button" aria-label="Open the menu" aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)} style={{ display: 'inline-flex', padding: SHELL_SPACE[1.5], border: '1px solid var(--t-border-color-medium)', borderRadius: 'var(--t-border-radius-sm)', background: 'var(--t-background-primary)', color: 'var(--t-font-color-secondary)', cursor: 'pointer' }}>
            <Icon name="menu" size={18} />
          </button>
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <img src="/ewings-logo-light.svg" alt="eWings" className="ewings-brand-logo-light" style={{ height: 26, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
            <img src="/ewings-logo-dark.svg" alt="eWings" className="ewings-brand-logo-dark" style={{ height: 26, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 'var(--t-font-size-sm)', color: 'var(--t-font-color-tertiary)' }}>{actingAs !== '' ? actingAs : profile.label}</span>
        </div>
        {drawerOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex' }}>
            <div style={{ height: '100%', width: 'min(280px, 86vw)', background: 'var(--t-background-primary)', boxShadow: 'var(--t-box-shadow-strong)', overflowY: 'auto' }}>{sidebar}</div>
            <div onClick={() => setDrawerOpen(false)} aria-hidden="true" style={{ flex: 1, background: 'rgba(15, 23, 42, 0.45)' }} />
          </div>
        )}
        {content}
      </PageBody>
    );
  }

  return (
    <PageBody scheme={scheme}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: SHELL_SPACE[4] }}>
        {sidebar}
        {content}
      </div>
    </PageBody>
  );
};

export default defineFrontComponent({
  universalIdentifier: PAGE_IDS.leadDesk.frontComponent,
  name: 'ewings-lead-desk',
  description: 'eWings lead desk: the admin, counsellor and telecaller views of the student pipeline, live from the CRM.',
  component: LeadDesk,
});
